import React, { useState, useEffect } from "react";
import Dropdown, { IOption } from "src/components/Dropdown/Dropdown";
import Button from "src/components/Button/Button";
import { apiService } from "src/services";
import { Constants, Deductions } from "@eagerdog/constants";
import { IEventSummary, IShow, IShowElement } from "@eagerdog/interfaces";
import styles from "./ExercisesTab.module.scss";

const EXERCISE_LEVELS = ["None", "I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X", "XI", "XII"];
const EXERCISE_OPTIONS: IOption[] = EXERCISE_LEVELS.map(l => ({ id: l === "None" ? "" : l, value: l }));
const EXERCISE_ELEMENTS = ["Open B", "Utility B", "Versatility"];

// Map display names to constants
const ELEMENT_MAPPING: { [key: string]: string } = {
  "Open B": Constants.dog_class_element_level.open_b,
  "Utility B": Constants.dog_class_element_level.utility_b,
  "Versatility": Constants.dog_class_element_level.versatility,
};

interface IProps {
  event: IEventSummary;
  show: IShow;
  onUpdate: () => void;
}

interface OrderState {
  [key: string]: string;
}

const ExercisesTab: React.FC<IProps> = ({ event, show, onUpdate }) => {
  const [order, setOrder] = useState<OrderState>(() => {
    const initial: OrderState = {};
    for (const el of EXERCISE_ELEMENTS) {
      const found = show.show_elements.find(
        (se: IShowElement) => (se.level || se.show_element) === el
      );
      initial[el] = found?.order_of_exercises || ""; // default to empty string (None)
    }
    return initial;
  });
  const [saving, setSaving] = useState(false);

  // Reload values when show prop changes to ensure current saved values are loaded
  useEffect(() => {
    const initial: OrderState = {};
    for (const el of EXERCISE_ELEMENTS) {
      const found = show.show_elements.find(
        (se: IShowElement) => (se.level || se.show_element) === el
      );
      initial[el] = found?.order_of_exercises || ""; // default to empty string (None)
    }
    setOrder(initial);
  }, [show]);

  const handleChange = (element: string, value: string) => {
    setOrder(prev => ({ ...prev, [element]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    const updatedShow: IShow = {
      ...show,
      show_elements: show.show_elements.map((se: IShowElement) => {
        const key = se.level || se.show_element;
        if (EXERCISE_ELEMENTS.includes(key)) {
          // Only set order_of_exercises if not empty string
          if (order[key]) {
            // Look up exercises from deductions
            const constantKey = ELEMENT_MAPPING[key];
            const exercisesFromDeductions = Deductions?.[Constants.sanctioning_club.AKC]?.[Constants.show_type.obedience]?.[constantKey]?.[order[key]] || [];
            
            // Transform string constants to objects with 'exercise' and 'disabled' properties
            const exercises = exercisesFromDeductions.map((exercise: string) => ({
              exercise: exercise,
              disabled: false
            }));
            
            return { 
              ...se, 
              order_of_exercises: order[key],
              exercises: exercises
            };
          } else {
            // Remove order_of_exercises and exercises if present
            const { order_of_exercises, exercises, ...rest } = se as any;
            return rest;
          }
        }
        return se;
      }),
    };
    try {
      await apiService.updateShow(event._id, show._id, updatedShow);
      onUpdate();
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className={styles.ExercisesTab} onSubmit={e => { e.preventDefault(); handleSave(); }}>
      <div className={styles.details}>
        <p>Set the order of exercises for each class below and click Save.</p>
      </div>
      {EXERCISE_ELEMENTS.map(element => (
        <div key={element} className={styles.ExerciseElement} style={{ marginBottom: 20 }}>
          <div className={styles.title}>
            <div className={styles.titleInner}>{element}</div>
            <div style={{ minWidth: 120 }}>
              <Dropdown
                value={{ id: order[element], value: order[element] || "None" }}
                options={EXERCISE_OPTIONS}
                onChange={(e: any, opt: IOption) => handleChange(element, opt.id)}
                label="Order"
                placeholder="Select order"
              />
            </div>
          </div>
        </div>
      ))}
      <div className={styles.actions}>
        <Button type="submit" disabled={saving}>{saving ? "Saving..." : "Save"}</Button>
      </div>
    </form>
  );
};

export default ExercisesTab; 